import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Popover, menuRow } from '@/components/ui/Popover';
import { Check, ChevronDown, Cog, ExternalLink, Pencil, ReportsIcon, Wiki } from '@/components/ui/icons';
import { cn } from '@/components/ui/cn';
import { useAppData } from '@/app-data';
import { ManageBoardsModal } from '@/components/manage-boards-modal';
import { BoardSettingsModal } from '@/components/board-settings-modal';

/**
 * Board picker in the top bar. Shares the same pill trigger + anchored menu as the
 * {@link ViewSwitcher} (Kanban / Table) so the two header dropdowns read identically.
 */
export default function BoardSwitcher() {
  const { boards, currentBoard, setCurrentBoardId } = useAppData();
  const [manage, setManage] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();

  if (!boards.length) return null;

  // The per-row cog opens Board settings for that board. The modal is bound to the current board
  // (statuses / prefix / access all resolve against it), so switching first makes it the target.
  const openSettings = (id: string, close: () => void) => {
    if (id !== currentBoard?.id) setCurrentBoardId(id);
    close();
    setSettingsOpen(true);
  };

  return (
    <>
      <Popover
        panelClassName="!max-h-none !overflow-hidden !p-0 w-[300px]"
        trigger={({ open, toggle }) => (
          <button
            type="button"
            onClick={toggle}
            aria-haspopup="menu"
            aria-expanded={open}
            className={cn(
              'inline-flex min-h-[36px] items-center gap-2 rounded-full px-3 text-[13px] font-semibold transition-colors',
              currentBoard ? 'bg-layer text-fg-strong' : 'text-fg-muted hover:bg-layer-strong hover:text-fg-soft',
            )}
          >
            <span className="max-w-[180px] truncate">{currentBoard?.name ?? 'Select board'}</span>
            <ChevronDown open={open} className="text-fg-faint" />
          </button>
        )}
      >
        {(close) => (
          <div className="flex max-h-[70vh] flex-col">
            <div className="thin-scroll min-h-0 flex-1 overflow-auto p-1.5">
              {boards.map((b) => {
                const isActive = b.id === currentBoard?.id;
                return (
                  <div key={b.id} className={cn(menuRow, 'group min-w-[210px] pr-1', isActive ? 'text-fg' : 'text-fg-soft')}>
                    <button
                      type="button"
                      onClick={() => {
                        if (!isActive) setCurrentBoardId(b.id);
                        close();
                      }}
                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    >
                      <span className="flex-1 truncate">{b.name}</span>
                      <span className="flex w-3.5 shrink-0 justify-center text-accent">{isActive ? <Check /> : null}</span>
                    </button>
                    {/* Cog: always shown for the current board, revealed on hover/focus for the rest. */}
                    <button
                      type="button"
                      onClick={() => openSettings(b.id, close)}
                      aria-label={`Settings for ${b.name}`}
                      title="Board settings"
                      className={cn(
                        'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-fg-faint transition-colors hover:bg-layer-strong hover:text-fg',
                        isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
                      )}
                    >
                      <Cog className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
            {/* Fixed footer — stays pinned while the board list scrolls above it. */}
            <div className="shrink-0 border-t border-line p-1.5">
              <a
                href="/whiteboards"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => close()}
                className="flex min-h-[42px] w-full items-center gap-2.5 rounded-lg px-2.5 text-[13px] font-semibold text-fg-soft transition-colors hover:bg-layer"
              >
                <Pencil className="h-4 w-4 shrink-0 text-fg-muted" />
                <span className="flex-1">Whiteboards</span>
                <ExternalLink className="h-3.5 w-3.5 shrink-0 text-fg-faint" />
              </a>
              <a
                href="/wiki"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => close()}
                className="flex min-h-[42px] w-full items-center gap-2.5 rounded-lg px-2.5 text-[13px] font-semibold text-fg-soft transition-colors hover:bg-layer"
              >
                <Wiki className="h-4 w-4 shrink-0 text-fg-muted" />
                <span className="flex-1">Wiki</span>
                <ExternalLink className="h-3.5 w-3.5 shrink-0 text-fg-faint" />
              </a>
              <a
                href="/reports"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => close()}
                className="flex min-h-[42px] w-full items-center gap-2.5 rounded-lg px-2.5 text-[13px] font-semibold text-fg-soft transition-colors hover:bg-layer"
              >
                <ReportsIcon className="h-4 w-4 shrink-0 text-fg-muted" />
                <span className="flex-1">Reports</span>
                <ExternalLink className="h-3.5 w-3.5 shrink-0 text-fg-faint" />
              </a>
              <div className="my-1 h-px bg-line" />
              <button
                type="button"
                onClick={() => {
                  navigate('/');
                  close();
                }}
                className="flex min-h-[40px] w-full items-center rounded-lg px-2.5 text-left text-[12px] text-fg-faint transition-colors hover:text-fg-soft"
              >
                All boards…
              </button>
              <button
                type="button"
                onClick={() => {
                  setManage(true);
                  close();
                }}
                className="flex min-h-[40px] w-full items-center rounded-lg px-2.5 text-left text-[12px] text-fg-faint transition-colors hover:text-fg-soft"
              >
                Manage boards…
              </button>
            </div>
          </div>
        )}
      </Popover>

      <ManageBoardsModal open={manage} onClose={() => setManage(false)} />
      <BoardSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
}
